import React, {useEffect} from 'react';
import PropTypes from "prop-types";
import {useFormik} from "formik";

import GuestItem from "../GuestItem";
import getValidationSchema from "./validator";

import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Checkbox from "@mui/material/Checkbox";
import Fab from "@mui/material/Fab";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import List from "@mui/material/List";
import MenuItem from "@mui/material/MenuItem";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";

import AddIcon from '@mui/icons-material/Add';
import {ResponsiveDialog} from "../../../../../components/ResponsiveDialog";
import {OptionalContent} from "../../../../../components/OptionalContent";
import {useCheckInCreate} from "../../../query";
import {useFeedbackTools} from "../../../../../components/FeedbackTools";
import EditIcon from "@mui/icons-material/Edit";
import IconButton from "@mui/material/IconButton";
import {startOfYesterday} from "date-fns";
import {DateFormatter} from "../../../../../services/date";
import Grid from "@mui/material/Grid";


const FormCheckIn = props => {
    const {open, booking, onClose, onNewGuest, onEditGuest} = props;
    const checkInCreate = useCheckInCreate({bookingId: booking.id});
    const {showSuccessNotification, showErrorNotification} = useFeedbackTools();

    const guests = React.useMemo(
        () => booking.getGuestReadyForCheckIn(),
        [booking]); // eslint-disable-line react-hooks/exhaustive-deps

    const allowedDates = React.useMemo(
        () => booking.getAllowedArrivalDates(),
        []); // eslint-disable-line react-hooks/exhaustive-deps

    const allowedDepartureDates = React.useMemo(
        () => booking.getAllowedDepartureDates({allowFutureDates: true}),
        []); // eslint-disable-line react-hooks/exhaustive-deps

    const formik = useFormik({
        initialValues: {
            checkInDate: allowedDates.length > 0 ? allowedDates[0].value : "",
            expectedCheckOutDate: allowedDepartureDates.length > 0 ? allowedDepartureDates[0].value : "",
            excludedGuests: [],
            syncAlloggiati: true,
            syncAbit: true
        },
        validationSchema: getValidationSchema(booking, guests),
        validateOnMount: true,
        onSubmit: async values => {
            const data = {
                checkInDate: values.checkInDate,
                expectedCheckOutDate: values.expectedCheckOutDate,
                guests: guests.map(g => g.id).filter(id => !values.excludedGuests.includes(id)),
                syncAlloggiati: values.syncAlloggiati,
                syncAbit: values.syncAbit
            }
            try {
                await checkInCreate.mutateAsync({data});
                showSuccessNotification("Check-In creato con successo");
                onClose();
            } catch (e) {
                console.error(e);
                showErrorNotification(e);
            }
        }
    });

    useEffect(() => {
        const guestIds = booking.guests.map(g => g.id);
        const updatedExcludedGuest = formik.values.excludedGuests.filter(v => guestIds.includes(v));
        formik.setFieldValue("excludedGuests", updatedExcludedGuest);
    }, [booking.guests]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleNewGuest = () => {
        onNewGuest();
    }

    const handleEditGuest = (guest) => {
        onEditGuest(guest);
    }

    const handleToggleExcludedGuest = (guestId) => () => {
        if (formik.values.excludedGuests.includes(guestId)) {
            formik.setFieldValue("excludedGuests", formik.values.excludedGuests.filter(v => v !== guestId))
        } else {
            formik.setFieldValue("excludedGuests", [...formik.values.excludedGuests, guestId])
        }
    }

    const submitIsEnabled = !Object.keys(formik.errors).some(x => formik.touched[x]);

    // Show the warning if the checkInDate date is before yesterday and the syncAlloggiati is true
    const yesterday = DateFormatter.onlyDateISOString(startOfYesterday());
    const alloggiatiCheckInDateWarning = formik.values.syncAlloggiati && formik.values.checkInDate < yesterday

    return (
        <ResponsiveDialog title="Check in"
                          isOpen={open}
                          maxWidth="md"
                          scroll="paper"
                          isLoading={checkInCreate.isLoading}
                          showApply={true}
                          applyLabel="Salva"
                          enableApply={submitIsEnabled}
                          onApply={formik.handleSubmit}
                          closeLabel="Cancella"
                          onClose={onClose}>

            {
                formik.touched.excludedGuests && Boolean(formik.errors.excludedGuests) &&
                <Alert severity="error" sx={{mb: 3}}>
                    <AlertTitle>Attenzione</AlertTitle>
                    {formik.errors.excludedGuests}
                </Alert>
            }


            <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                    <TextField
                        select
                        label="Data di arrivo"
                        name="checkInDate"
                        fullWidth
                        value={formik.values.checkInDate}
                        onChange={formik.handleChange}
                        error={formik.touched.checkInDate && Boolean(formik.errors.checkInDate)}
                        helperText={formik.touched.checkInDate && formik.errors.checkInDate}
                        sx={{mt: 1}}>
                        {
                            allowedDates.map((d, i) => (<MenuItem key={i} value={d.value}>{d.label}</MenuItem>))
                        }
                    </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                        select
                        label="Data di partenza (attesa)"
                        name="expectedCheckOutDate"
                        fullWidth
                        value={formik.values.expectedCheckOutDate}
                        onChange={formik.handleChange}
                        error={formik.touched.expectedCheckOutDate && Boolean(formik.errors.expectedCheckOutDate)}
                        helperText={formik.touched.expectedCheckOutDate && formik.errors.expectedCheckOutDate}
                        sx={{mt: 1}}>
                        {
                            allowedDepartureDates.map((d, i) => (<MenuItem key={i} value={d.value}>{d.label}</MenuItem>))
                        }
                    </TextField>
                </Grid>
            </Grid>

            <Typography variant="h6" sx={{color: 'primary.main', mt: 4, mb: 1}}>
                Ospiti
            </Typography>

            <OptionalContent hasContent={guests.length !== 0} showIcon={true} noContentMessage="Nessun ospite inserito">
                <List disablePadding sx={{mt: 1}}>
                    {guests.map((g, i) => (
                        <ListItem
                            key={i}
                            disablePadding
                            divider={i !== guests.length - 1}
                            secondaryAction={
                                <IconButton size="small" edge="end" onClick={() => handleEditGuest(g)}>
                                    <EditIcon fontSize="small"/>
                                </IconButton>
                            }>
                            <ListItemIcon sx={{minWidth: 40}}>
                                <Checkbox onChange={handleToggleExcludedGuest(g.id)}
                                          checked={!formik.values.excludedGuests.includes(g.id)}
                                          edge="start"/>
                            </ListItemIcon>
                            <GuestItem guest={g}
                                       defaultArrivalDate={new Date(formik.values.checkInDate)}
                                       defaultDepartureDate={new Date(formik.values.expectedCheckOutDate)}
                                       showStatus={false} disableHorizontalPadding={true} showTouristTax />
                        </ListItem>
                    ))}
                </List>
            </OptionalContent>

            <Box sx={{display: "flex", justifyContent: "center"}}>
                <Fab onClick={handleNewGuest}
                     variant="extended"
                     size="small"
                     color="primary"
                     aria-label="add-guest"
                     sx={{mt: 5, mb: 2, pr: 2}}>
                    <AddIcon sx={{mr: 1}}/>
                    Aggiungi ospite
                </Fab>
            </Box>

            <Card variant="outlined" sx={{mt: 2}}>
                <CardContent>
                    <Typography variant="h6" sx={{color: 'primary.main'}}>
                        Azioni rapide
                    </Typography>
                    <FormGroup sx={{mt: 1}}>
                        <FormControlLabel
                            control={<Checkbox name="syncAlloggiati" checked={formik.values.syncAlloggiati}
                                               onChange={formik.handleChange}/>}
                            label="Carica schedine sul servizio Alloggiati (Polizia di Stato)"/>
                        <FormControlLabel control={<Checkbox name="syncAbit" checked={formik.values.syncAbit}
                                                             onChange={formik.handleChange}/>}
                                          label="Carica gli arrivi sul servizio Abit"/>
                    </FormGroup>
                </CardContent>
            </Card>

            {
                alloggiatiCheckInDateWarning && <Alert severity="warning" sx={{mt: 3}}>
                    La data di arrivo selezionata è antecedente a ieri. Le schedine saranno caricate sul servizio Alloggiati
                    con la data di ieri.
                </Alert>
            }
        </ResponsiveDialog>
    );
};

FormCheckIn.propTypes = {
    open: PropTypes.bool.isRequired,
    booking: PropTypes.object.isRequired,
    onClose: PropTypes.func.isRequired,
    onNewGuest: PropTypes.func.isRequired,
    onEditGuest: PropTypes.func.isRequired,
}

export default FormCheckIn;